<script setup lang="ts" generic="T extends {id: string | number}">
import { computed, provide, useSlots } from 'vue';
import { useI18n } from 'vue-i18n';

import CaretRight from '@/components/svg/CaretRight.vue';
import DataTableSelectionCell from '@/components/tables/DataTableSelectionCell.vue';

type Props<T> = {
  items: T[];
  keyField: keyof T;
  showActions?: boolean;
  selection?: 'single' | 'multiple';
  selectionDisabled?: (item: T) => boolean;
}

const { t } = useI18n();

defineOptions({
  inheritAttrs: false,
});

const model = defineModel<T>();
const hoveredModel = defineModel<T | undefined>('hovered');
const selectedItems = defineModel<T[]>('selectedItems', { default: () => [] });
const props = defineProps<Props<T>>();
const slots = useSlots();

const emit = defineEmits<{
  'row-action': [item: T];
  'row-click': [item: T];
  'selection-change': [selected: T | T[]];
  'select-all': [items: T[]];
  'deselect-all': [];
}>();

provide('dataTable', {
  handleRowAction,
  items: props.items
});

const columns = computed(() => {
  const defaultSlot = slots.default?.();
  if (!defaultSlot) return [];

  return defaultSlot.filter(vnode =>
    vnode.type &&
    typeof vnode.type === 'object' && 
    'name' in vnode.type && 
    vnode.type.name === 'TableColumn'
  );
});

function handleRowClick(item: T) {
  if (props.selection === 'single') {
    emit('selection-change', item);
  }
  emit('row-click', item);
}

function handleRowAction(item: T) {
  emit('row-click', item);
}

function handleRowMouseEnter(item: T) {
  hoveredModel.value = item;
}

function handleRowMouseLeave() {
  hoveredModel.value = undefined;
}

function handleSelectionChange(item: T, isSelected: boolean) {
  if (props.selection === 'single') {
    model.value = isSelected ? item : undefined;
    emit('selection-change', model.value!);
  } else if (props.selection === 'multiple') {
    const currentSelection = [...(selectedItems.value || [])];
    
    if (isSelected) {
      if (!currentSelection.find(selected => selected[props.keyField] === item[props.keyField])) {
        currentSelection.push(item);
      }
    } else {
      const index = currentSelection.findIndex(selected => selected[props.keyField] === item[props.keyField]);
      if (index > -1) {
        currentSelection.splice(index, 1);
      }
    }
    
    selectedItems.value = currentSelection;
    emit('selection-change', currentSelection);
  }
}

function handleSelectAll() {
  if (props.selection === 'multiple') {
    selectedItems.value = [...selectableItems.value];
    emit('select-all', selectableItems.value);
    emit('selection-change', selectableItems.value);
  }
}

function handleDeselectAll() {
  if (props.selection === 'multiple') {
    selectedItems.value = [];
    emit('deselect-all');
    emit('selection-change', []);
  }
}

function isItemSelected(item: T): boolean {
  if (props.selection === 'single') {
    return model.value?.[props.keyField] === item[props.keyField];
  }
  return selectedItems.value?.some(selected => selected[props.keyField] === item[props.keyField]) ?? false;
}

function isItemSelectable(item: T): boolean {
  return !props.selectionDisabled?.(item);
}

const selectableItems = computed(() => {
  return props.items.filter(item => isItemSelectable(item));
});

const isAllSelected = computed(() => {
  if (props.selection !== 'multiple' || props.items.length === 0) {
    return false;
  }
  return selectableItems.value.length > 0 && selectableItems.value.every(item => isItemSelected(item));
});

const isIndeterminate = computed(() => {
  if (props.selection !== 'multiple') {
    return false;
  }
  const selectedCount = selectedItems.value?.length ?? 0;
  return selectedCount > 0 && selectedCount < selectableItems.value.length;
});

const totalColumns = computed(() => {
  let count = columns.value.length;
  if (props.selection) count += 1;
  if (props.showActions) count += 1;
  return count;
});
</script>

<template>
  <div class="dataTable-container">
    <table class="dataTable"
           v-bind="$attrs">
      <thead>
        <tr>
          <template v-for="(column, index) in columns"
                    :key="index">
            <component :is="column"
                       mode="header" />
          </template>

          <th v-if="selection"
              class="dataTable-header dataTable-selectionColumn">
            <DataTableSelectionCell v-if="selection === 'multiple'"
                                    mode="header"
                                    :is-all-selected="isAllSelected"
                                    :is-indeterminate="isIndeterminate"
                                    @select-all="handleSelectAll"
                                    @deselect-all="handleDeselectAll" />
          </th>

          <th v-if="showActions"
              aria-label="Actions"
              class="dataTable-header dataTable-header_actionHeader"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in items"
            :key="(item[keyField as keyof T] as string | number)"
            class="dataTable-row"
            :data-selected="model === item"
            :data-hovered="hoveredModel === item"
            @click="handleRowClick(item)"
            @mouseenter="handleRowMouseEnter(item)"
            @mouseleave="handleRowMouseLeave()">
          <template v-for="(column, index) in columns"
                    :key="index">
            <component :is="column"
                       :item="item"
                       :field="column.props?.field"
                       mode="cell" />
          </template>

          <DataTableSelectionCell v-if="selection && isItemSelectable(item)"
                                  class="dataTable-cell dataTable-selectionColumn"
                                  mode="cell"
                                  :selection-mode="selection"
                                  :is-selected="isItemSelected(item)"
                                  :item="item"
                                  @selection-change="handleSelectionChange" />
          <td v-else-if="selection" 
              class="dataTable-cell dataTable-selectionColumn"></td>

          <td v-if="showActions"
              class="dataTable-cell dataTable-cell_action">
            <slot name="action"
                  :item="item">
              <button class="btn btn_small btn_ghost dataTable-actionButton"
                      aria-label="Row action"
                      @click="handleRowAction(item)">
                <CaretRight class="dataTable-arrowIcon"
                            width="16"
                            height="16" />
              </button>
            </slot>
          </td>
        </tr>
        <tr v-if="items.length === 0">
          <td :colspan="totalColumns"
              class="dataTable-cell dataTable-cell_emptyState">
            <slot name="empty">
              {{ t('common.noDataAvailable') }}
            </slot>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss">
.dataTable-container {
  --tableInlinePadding: 16px;
  width: 100%;

  container: dataTable / inline-size;
}

.dataTable {
  --selectionIconSize: 20px;

  table-layout: fixed;
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;

  font-size: 0.9em;

  :is(td, th) {
    padding: 0.75rem var(--spc24-16);
    text-align: start;
    border-top: 1px solid transparent;
    border-bottom: 1px solid #e2e8f0;

    &:first-child {
      padding-inline-start: var(--tableInlinePadding);
    }

    &:last-child {
      padding-inline-end: var(--tableInlinePadding);
    }
  }

  :is([data-value-type="number"], [data-value-type="currency"]) {
    text-align: right;
  }
}

.dataTable-header {
  font-weight: 600;
  vertical-align: bottom;
}

.dataTable-row {
  --cardColorBackground: var(--gray-100);

  transition-property:
    background-color,
    border-color;
  transition-duration: 0.1s;
  transition-timing-function: ease-in-out;

  &:nth-child(odd) {
    background-color: oklch(from var(--cardColorBackground) l c h / 0.5);
  }

  &:is(:hover, [data-hovered="true"]) {
    background-color: var(--cardColorBackground);
    
    cursor: pointer;
  }

  &[data-selected="true"] {
    background-color: var(--cardColorBackground);

    td {
      border-block: 1px solid var(--primary-500);
    }

    .dataTable-row:has(+ &) {
      :is(td, th) {
        border-bottom: none;
      }
    }
  }

  thead:has(+ tbody > &:first-child) th {
    border-bottom: none;
  }
}

:is(td, th).dataTable-numericColumn {
  text-align: right;
}

.dataTable-selectionColumn {
  width: 60px;
  text-align: center;
}

.dataTable-header_actionHeader {
  width: 60px;
}

.dataTable-actionCell {
  width: 60px;
  text-align: right;
}

.dataTable-cell_emptyState {
  text-align: center;
  padding: 2rem;
}

@media screen and (max-width: 1400px) {
  .dataTable {
    thead {
      display: none;
    }

    tbody {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(min(100%, 25rem), 1fr));
      gap: 1.6rem;
    }

    tr {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 0.8rem;
      padding: 1.6rem;
      border: 1px solid var(--contentColorBorder);
      border-radius: var(--radius-md);
      background-color: var(--cardColorBackground);

      &:nth-child(odd) {
        background-color: var(--cardColorBackground);
      }

      &:hover {
        background-color: oklch(from var(--cardColorBackground) calc(l * .98) c h / .8);
      }

      &:is(:hover, [data-selected="true"]) {
        box-shadow: none;
        border-color: var(--primary-500);
      }

      &[data-selected="true"] {
        outline: 1px solid var(--primary-500);
      }
    }

    td.dataTable-cell {
      width: auto;
      padding: 0 2.5rem 0 0;
      border: none;
      text-align: start;
    }

    .dataTable-selectionCell {
      position: absolute;
      top: 1.6rem;
      right: 1.6rem;
      width: fit-content;

      &:last-child {
        padding-inline-end: 0;
      }
    }
  }
}
</style>
